import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Stack } from "@mui/material";
import { api, getWorkingOrg, setWorkingOrg } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { relative } from "@/lib/format";
import { AREA_SHORT, areas as catalogue, mine } from "./api";

/** While MultiDoc staff work inside a partner, say so on every page. */
export default function WorkingInsideBanner() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [org, setOrg] = useState<string | null>(getWorkingOrg());
  useEffect(() => {
    const on = (e: Event) => setOrg((e as CustomEvent<string | null>).detail ?? null);
    window.addEventListener("mdc:working-org", on);
    return () => window.removeEventListener("mdc:working-org", on);
  }, []);
  const vendor = me?.org.kind === "vendor";
  const mode = useQuery({ queryKey: ["api", "access", "areas"], queryFn: catalogue, enabled: vendor && Boolean(org) });
  const active = vendor && Boolean(org) && org !== me?.org.id && mode.data?.enforced === true;
  const status = useQuery({
    queryKey: ["api", "access", "mine", org], queryFn: () => mine(org!), enabled: active,
    refetchInterval: 60_000,
  });
  const partner = useQuery({
    queryKey: ["api", "access", "org-name", org],
    queryFn: () => api<{ legal_name: string; brand_name?: string }>(`/resellers/${org}`,
                                                                    { headers: { "X-Org-Id": "" } }),
    enabled: active,
  });
  if (!active) return null;
  const name = partner.data?.brand_name || partner.data?.legal_name || "a partner";
  const areas = status.data?.areas ?? [];
  const ends = status.data?.grants.map((g) => g.expires_at).filter(Boolean).sort()[0];
  const leave = () => { setWorkingOrg(null); navigate("/"); };
  return (
    <Alert severity={areas.length ? "warning" : "error"} sx={{ mb: 2 }} data-tour="working-inside"
           action={
             <Stack direction="row" spacing={1}>
               {!areas.length ? (
                 <Button color="inherit" size="small" onClick={() => navigate(`/partners/${org}?tab=privacy`)}>
                   Ask for access
                 </Button>
               ) : null}
               <Button color="inherit" size="small" variant="outlined" onClick={leave}>Leave</Button>
             </Stack>
           }>
      {areas.length
        ? `Working inside ${name}: ${areas.map((a) => AREA_SHORT[a]).join(", ")}${ends ? `; access ends ${relative(ends)}` : ""}. Everything you open is written to ${name}'s access log.`
        : `You are inside ${name}, but ${name} has not opened anything to you. Pages here will be refused.`}
    </Alert>
  );
}
